import React from 'react';
import Navbar from '../components/Navbar';
import ContactForm from '../components/ContactForm';
import { APP_URL } from '../lib/appUrl';

/**
 * /contact — the general-purpose form. Press, partnerships, venues, anyone.
 *
 * There is no address on this page and there must not be one: contact is
 * form-only across the whole product, so every message arrives in the admin
 * inbox with a status and a reference instead of landing in a mailbox nobody
 * else can see. /support runs the same form with support-shaped routing notes.
 */
export default function Contact() {
    return (
        <div className="min-h-screen grain scanlines">
            <Navbar />

            <section className="border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-12 lg:pt-28 lg:pb-16">
                    <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-6 fade-up">
                        // Reach us
                    </p>
                    <h1 className="font-display uppercase tracking-tight leading-[0.85] text-white fade-up" data-testid="contact-title">
                        <span className="block text-6xl sm:text-8xl lg:text-9xl">Tell us</span>
                        <span className="block text-6xl sm:text-8xl lg:text-9xl text-neon neon-glow">about it.</span>
                    </h1>
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
                    {/* LEFT — context */}
                    <div className="lg:col-span-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-4">// Direct line</p>
                        <h2 className="font-display uppercase text-3xl tracking-tight leading-[0.95] mb-6">
                            Press, partnerships,<br />or just <span className="text-neon">say hi.</span>
                        </h2>
                        <p className="text-white/70 leading-relaxed text-sm mb-8">
                            Every message lands directly in our admin dashboard. We read all of them and reply
                            from a real person — usually within a working day. You get a confirmation with a
                            reference number the moment you send.
                        </p>
                        <div className="space-y-4 text-sm">
                            <div className="border-t border-white/10 pt-4">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">// For organizers</p>
                                <a href={APP_URL} className="text-white/80 hover:text-neon underline underline-offset-4 decoration-dotted">
                                    Use Support inside the producer console →
                                </a>
                            </div>
                            <div className="border-t border-white/10 pt-4">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">// Refund requests</p>
                                <p className="text-white/60 leading-relaxed">
                                    Portal does not process refunds. <span className="text-white">Only the event
                                    organizer can issue a refund</span> for their event, per their own refund policy.
                                    Open your ticket in your Portal wallet and use the &ldquo;Contact organizer&rdquo;
                                    option there.
                                </p>
                            </div>
                            <div className="border-t border-white/10 pt-4">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">// Attendee questions</p>
                                <p className="text-white/60 leading-relaxed">
                                    Anything about a specific ticket — entry, transfers, lost QR — also goes through
                                    the organizer of your event from inside your wallet.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — form */}
                    <div className="lg:col-span-8">
                        {/* Refund-policy notice — visible above the form so it's impossible to miss */}
                        <div
                            data-testid="contact-refund-notice"
                            className="border border-neon/30 bg-neon/[0.04] px-5 py-4 mb-6 flex items-start gap-3"
                        >
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon font-bold pt-0.5 flex-shrink-0">
                                FYI
                            </span>
                            <p className="text-white/80 text-sm leading-relaxed">
                                <span className="text-white font-medium">Refund requests aren&apos;t handled here.</span>{' '}
                                Only the event organizer can issue a refund for their event — please contact them
                                directly through your Portal wallet.
                            </p>
                        </div>

                        <ContactForm defaultCategory="other" testIdPrefix="contact" />
                    </div>
                </div>
            </section>

            <footer className="border-t border-white/10 py-10 text-center text-[10px] text-white/40 font-mono uppercase tracking-[0.3em]">
                © Portal 2026 // Built for nights worth losing
            </footer>
        </div>
    );
}
